<template>
  <compensatory-request-search
    :compensatory-request-search="compensatoryRequestSearch"
    :compensatory-request-search-init-filters="
      compensatoryRequestSearchInitFilters
    "
    :general-api="generalApi"
    :get-compensatory-request-general-info="getCompensatoryGeneralInfo"
    :get-compensatory-request-replacement="getCompensatoryRequestInterventions"
    :get-determination-reasons="getDeterminationReasons"
    :get-districts="getDistricts"
    :get-documents-by-student-service="getDocumentsByStudentService"
    :get-educational-documents-by-request="getEducationalDocumentsByRequest"
    :get-municipalities="getMunicipalities"
    :get-providers-by-academicyear-id="getProvidersByAcademicyearId"
    :get-regions="getRegions"
    :get-schools="getSchools"
    :get-service-locations="getServiceLocations"
    :get-specialist-by-provider-id="specialistsList"
    :get-visits="getVisits"
    :has-approval-permission="hasApprovalPermission"
    :has-closing-analysis-permission="hasClosingAnalysisPermission"
    :has-delete-permission="hasDeletePermission"
    :has-pending-approvement-last-status-permission="
      hasPendingApprovementLastStatusPermission
    "
    :has-pending-approvement-permission="hasPendingApprovementPermission"
    :is-intervention-are-approved="isInterventionAreApproved"
    :is-specialist="isSpecialist"
    :location-search-config-filters="locationSearchConfigFilters"
    :on-add-navigation="onAddNavigationHandler"
    :on-delete-request="onDeleteRequest"
    :on-determination-submit="onDeterminationSubmit"
    :on-download="onDownload"
    :on-download-referral-sheet="downloadReferralSheet"
    :on-history-navigation="onHistoryNavigation"
    :on-replace-document="replaceDocument"
    :on-request-navigation="onRequestNavigation"
    :on-visit-certification="getVisitsCertification"
    :on-visit-registry-navigation="onVisitRegistryNavigateHandler"
    :print-form="onPrintForm"
    :save-referral-item-attachment="saveReferralItemAttachment"
    :update-compensatory-request-status="updateCompensatoryRequestStatus"
    :update-request-status-to-previous="updateRequestStatusToPrevious"
    :view-add-action="viewAddAction"
    :view-determination-options="false"
    :view-menu-print-form="viewMenuPrintForm"
    @on-click-return="onReturnHandler"
  />
</template>

<script>
import moment from 'moment';
import 'moment/locale/es-do';
import { CompensatoryRequestSearch } from 'compensatory-service';
import {
  compensatoryRequestSearchInitFilters as _compensatoryRequestSearchInitFilters,
  compensatoryRequestSearch as _compensatoryRequestSearch,
  getCompensatoryRequestInterventions as _getCompensatoryRequestInterventions,
  getVisits as _getVisits,
  saveReferralItemAttachment as _saveReferralItemAttachment,
  getReferralDocumentsByStudentService as _getDocumentsByStudentService,
  getEducationalDocumentsByRequest as _getEducationalDocumentsByRequest,
  updateRequestStatusToPrevious as _updateRequestStatusToPrevious,
  updateCompensatoryRequestStatus as _updateCompensatoryRequestStatus,
  submitDetermination as _submitDetermination,
  getForm,
  getCompensatoryRequestGeneralInfo as _getCompensatoryRequestGeneralInfo,
  getDeterminationReasons as _getDeterminationReasons,
  isInterventionAreApproved as _isInterventionAreApproved,
  deleteCompensatoryRequest as _deleteCompensatoryRequest,
  replaceDocument as _replaceDocument,
} from '@/modules/compensatoryService/api';
import {
  getMunicipalities as _getMunicipalities,
  getSchools as _getSchools,
  getRegions as _getRegions,
  getDistricts as _getDistricts,
} from '@/modules/common/location.api';
import { getList as _getServiceLocations } from '@/modules/shared/servicelocation.api';
import { getProvidersByAcademicyearId as _getProvidersByAcademicyearId } from '@/modules/shared/provider.api';
import { getVisitStudentInfo as _getVisitStudentInfo } from '@/modules/billingHistory/api';
import peiServices from '@/modules/app/api.js';
import compensatoryRequestTypes from '@/utils/constants/compensatoryRequestTypes';
import compensatoryServiceStatus from '@/utils/constants/compensatoryServiceStatus.js';
import { getReferralSheet as _getReferralSheet } from '@/modules/shared/studentreferral.api';
import _visitRegistryApi from '@/modules/visitRegistry/api.js';
import _generalApi from '@/modules/app/api.js';
import { specialistsList as _specialistsList } from '@/modules/provider/api';
import roles from '@/utils/constants/roles.js';
import { name as userModuleName } from '@/modules/user/store';
import { mapState } from 'vuex';
import { mapGetters } from 'vuex';

const VISIT_VALIDATION_ROUTE_NAME = 'compensatoryVisitRegistryValidate';

export default {
  components: {
    CompensatoryRequestSearch,
  },
  computed: {
    ...mapState(userModuleName, ['user']),
    ...mapGetters('$_user', ['fullProfile']),
    hasPendingApprovementPermission() {
      return this.userHasPermissions(
        'compensatoryservice.setpendingapprovementstatus'
      );
    },
    isSpecialist() {
      return this.fullProfile?.specialistId != null;
    },
    hasApprovalPermission() {
      return this.userHasPermissions('compensatory.transition.status.approved');
    },
    hasDeletePermission() {
      return this.userHasPermissions('provider.compensatory.request.delete');
    },
    viewMenuPrintForm() {
      return ({ item }) =>
        item.requestTypeId == compensatoryRequestTypes.Reposicion &&
        [
          compensatoryServiceStatus.Aprobado,
          compensatoryServiceStatus.Denegado,
          compensatoryServiceStatus.AprobadoModificado,
          compensatoryServiceStatus.Sometido,
          compensatoryServiceStatus.ProcesoCompletado,
          compensatoryServiceStatus.SolicitudRechazada,
          compensatoryServiceStatus.CompletadoPendienteAprobacion,
          compensatoryServiceStatus.DevueltoAProveedor,
          compensatoryServiceStatus.Excluido,
        ].includes(item.statusId);
    },
    hasPendingApprovementLastStatusPermission() {
      return this.userHasPermissions(
        'compensatory.laststatus.pendingapprovement'
      );
    },
    hasClosingAnalysisPermission() {
      return this.userHasPermissions(
        'provider.compensatoryservice.canviewclosinganalysis'
      );
    },
    viewAddAction() {
      return this.userHasPermissions('provider.compensatoryService.add');
    },
    generalApi() {
      return _generalApi;
    },
    locationSearchConfigFilters() {
      return {
        skipUserCentersFlag: this.isCentro,
        skipUserRegionsFlag: this.isCentro,
        skipUserDistrictsFlag: this.isCentro,
        skipUserMunicipalitiesFlag: this.isCentro,
        skipUserSchoolCodesFlag: this.isCentro,
        skipUserSchoolsFlag: this.isCentro,
      };
    },
    isCentro() {
      return this.userRole === roles.Centro;
    },
  },
  methods: {
    async isInterventionAreApproved(requestId) {
      return _isInterventionAreApproved(requestId);
    },
    async replaceDocument(payload) {
      const documentId = payload?.get('documentId');
      return _replaceDocument(documentId, payload);
    },
    getDeterminationReasons() {
      return _getDeterminationReasons();
    },
    onDeterminationSubmit(item) {
      return _submitDetermination(item);
    },
    compensatoryRequestSearchInitFilters() {
      return _compensatoryRequestSearchInitFilters();
    },
    compensatoryRequestSearch(model, newPage, currentPage) {
      return _compensatoryRequestSearch(model, newPage, currentPage);
    },
    getCompensatoryRequestInterventions(id) {
      return _getCompensatoryRequestInterventions(id);
    },
    getProvidersByAcademicyearId(academicYearId) {
      return _getProvidersByAcademicyearId(academicYearId);
    },
    specialistsList(params) {
      return _specialistsList(params);
    },
    getServiceLocations({ providerId }) {
      return _getServiceLocations(providerId);
    },
    getRegions(filters) {
      return _getRegions(filters);
    },
    getDistricts(filters) {
      return _getDistricts(filters);
    },
    getMunicipalities(filters) {
      return _getMunicipalities(filters);
    },
    getSchools(filters) {
      return _getSchools(filters);
    },
    getVisits(admissionId, requestType) {
      return _getVisits(admissionId, requestType);
    },
    saveReferralItemAttachment(params) {
      return _saveReferralItemAttachment(params);
    },
    updateRequestStatusToPrevious(params) {
      return _updateRequestStatusToPrevious(params);
    },
    updateCompensatoryRequestStatus(params) {
      return _updateCompensatoryRequestStatus(params);
    },
    getVisitsCertification(params) {
      return _visitRegistryApi.getVisitsCertificationCompensatory(params);
    },
    onAddNavigationHandler(item) {
      this.$router.push({
        name: 'CompensatorySearchService',
        query: {
          ...item,
        },
      });
    },
    async onRequestNavigation(item) {
      const request = {
        academicYearId: item.academicYearId,
        requestId: item.id,
        admissionId: item.admissionId,
        studentServiceId: item.studentServiceId,
        isReposition: item.requestTypeId == 1,
        studentName: item.studentName,
        studentSie: item.studentSie,
        serviceSpecialistName: item.specialistName,
        disciplineName: item.disciplineName,
        isEducational: item.isEducational,
      };
      this.$router.push({
        name: 'CompensatoryRequest',
        query: {
          isReposition:
            item.requestTypeId == compensatoryRequestTypes.Reposicion,
          isEducational: item.isEducational,
        },
        params: {
          ...request,
          ...this.$route.params,
          readOnly: item.statusId !== compensatoryServiceStatus.Nuevo,
        },
      });
    },
    async onHistoryNavigation(item) {
      const request = {
        requestId: item.id,
        admissionId: item.admissionId,
        studentServiceId: item.studentServiceId,
        isReposition: item.requestTypeId == 1,
        studentName: item.studentName,
        serviceSpecialistName: item.specialistName,
        disciplineName: item.disciplineName,
      };
      this.$router.push({
        name: 'CompensatoryRequestStatusHistory',
        query: {
          isReposition:
            item.requestTypeId == compensatoryRequestTypes.Reposicion,
          isEducational: item.isEducational,
        },
        params: {
          ...request,
          ...this.$route.params,
          readOnly: item.statusId !== compensatoryServiceStatus.Nuevo,
        },
      });
    },
    getVisitDate(year, month) {
      return moment()
        .set({
          day: 1,
          month: month - 1,
          year: year,
        })
        .format('MMMM YYYY');
    },
    async onVisitRegistryNavigateHandler(item) {
      if (item) {
        const monthNumber = 6;
        const academicYear = item.academicYear;
        const academicYearId = item.academicYearId;
        const academicYearDescription = item.academicYearDescription;
        const admissionId = item.admissionId;
        const studentServiceId = item.studentServiceId;
        const monthDate = this.getVisitDate(academicYear, monthNumber);
        const academicYearDesFrom = parseInt(
          academicYearDescription.split('-')[1]
        );
        const currentPeriodYear = moment(item.academicYearStartDate).year();

        //get student visit info
        const { data: studentInfo } = await _getVisitStudentInfo(
          studentServiceId,
          academicYearId,
          monthDate,
          admissionId
        );

        this.$router.push({
          name: VISIT_VALIDATION_ROUTE_NAME,
          params: {
            ...studentInfo,
            admissionId,
            academicYearId: academicYearId,
            visitStudentServiceId: studentServiceId,
            serviceDate: monthDate,
            initialPeriodInfo: {
              monthNumber,
              year: currentPeriodYear,
            },
            currentPeriod: {
              monthNumber,
              year: academicYearDesFrom,
            },
            isCompensatory: true,
            compensatoryInfo: {
              requestType: item.requestType,
              requestTypeId: item.requestTypeId,
              requestNumber: item.requestNumber,
              interventionAmount: item.compensatoryInterventionAmount,
              serviceSpecialistId: item.specialistId,
            },
            disabledPeriods: [],
            filter: item.filter,
          },
        });
      }
    },
    onDownload(documentId) {
      return peiServices.downloadDocumentByAttachmentId(documentId);
    },
    async onPrintForm(item) {
      await getForm(item.id, item.isEducational).then((response) => {
        peiServices.ConvertToBlob(response.data);
      });
    },
    getDocumentsByStudentService(payload, currentPage, pageSize) {
      return _getDocumentsByStudentService(payload, currentPage, pageSize);
    },
    getEducationalDocumentsByRequest(id, currentPage, pageSize) {
      return _getEducationalDocumentsByRequest(id, currentPage, pageSize);
    },
    async downloadReferralSheet(studentServiceId) {
      if (studentServiceId) {
        await _getReferralSheet(studentServiceId).then((response) => {
          peiServices.ConvertToBlob(response.data);
        });
      }
    },
    onReturnHandler() {
      this.$router.push({
        name: 'provider',
      });
    },
    getCompensatoryGeneralInfo(params) {
      return _getCompensatoryRequestGeneralInfo(params);
    },
    onDeleteRequest(requestId) {
      return _deleteCompensatoryRequest(requestId);
    },
  },
};
</script>
<style>
/** Esto es por callout (Refactorize next version) */
.overflow-hidden {
  overflow: visible !important;
}
</style>
